import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthDialog from "./AuthDialog";
import { NAV } from "@/constants/testIds";

const TABS = [
  {
    to: "/family-trees",
    label: "India Family Trees",
    testid: NAV.tabTrees,
    children: [
      { to: "/family-trees", label: "Featured trees", testid: NAV.tabTreesFeatured },
      { to: "/my-tree", label: "Build my tree", testid: NAV.tabTreesBuilder },
    ],
  },
  {
    to: "/celebrate-a-life",
    label: "Celebrate a Life",
    testid: NAV.tabCelebrate,
    children: [
      { to: "/celebrate-a-life#memorials", label: "Memorials wall", testid: NAV.tabCelebrateMemorials },
      { to: "/celebrate/in_memoriam", label: "In Memoriam feed" },
      { to: "/celebrate/obituary", label: "Obituary feed" },
      { to: "/celebrate/memorial", label: "Memorial feed" },
      { to: "/celebrate/birthday", label: "Birthdays feed" },
      { to: "/celebrate/anniversary", label: "Anniversaries feed" },
      { to: "/celebrate/naming", label: "Naming ceremonies" },
      { to: "/celebrate/graduation", label: "Graduations" },
      { to: "/celebrate/retirement", label: "Retirements" },
      { to: "/celebrate/special", label: "Special occasions" },
    ],
  },
  { to: "/famous-families", label: "Famous Indian Families", testid: NAV.tabFamous },
  { to: "/indian-heritage", label: "Indian Heritage", testid: NAV.tabHeritage },
  {
    to: "/cards",
    label: "Create a Card",
    testid: NAV.tabCards,
    children: [
      { to: "/cards", label: "Card studio" },
      { to: "/my-cards", label: "My cards" },
      { to: "/my-portraits", label: "My portraits" },
    ],
  },
  { to: "/explore-india", label: "Explore India", testid: NAV.tabExplore },
  { to: "/about", label: "About", testid: NAV.tabAbout },
  { to: "/contact", label: "Contact", testid: NAV.tabContact },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("register");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown, setDropdown] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropdown(null); }, [location.pathname]);

  const activeTop = (path) => location.pathname === path || (path !== "/" && location.pathname.startsWith(path));

  return (
    <>
      <motion.header
        data-testid={NAV.root}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled || location.pathname !== "/"
            ? "bg-ivory/85 backdrop-blur-xl border-b border-ivory-300"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-3 group shrink-0"
          >
            <svg viewBox="0 0 40 40" className="w-11 h-11 text-tulsi" fill="none" stroke="currentColor" strokeWidth="1.4">
              <circle cx="20" cy="10" r="3.2" />
              <circle cx="10" cy="24" r="3.2" />
              <circle cx="30" cy="24" r="3.2" />
              <circle cx="20" cy="34" r="3" />
              <path d="M20 13v6M17 21 12 24M23 21l5 3M12 27l7 5M28 27l-7 5" />
            </svg>
            <div className="leading-none">
              <div className="font-serif text-2xl md:text-[26px] tracking-tight text-ink whitespace-nowrap">
                IndianFamilyTree<span className="text-saffron">.com</span>
                <sup className="text-[10px] text-ink-50 font-sans align-super ml-0.5 tracking-normal">™</sup>
              </div>
              <div className="mt-1.5 font-serif italic text-[11px] md:text-[12px] leading-[1.35] whitespace-nowrap">
                <div className="text-saffron-deep">Indians, Discover Your Roots.</div>
                <div className="text-tulsi">Celebrate Your Heritage.</div>
                <div className="text-[#1E4E8A]">Preserve Your Legacy.</div>
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-2 flex-1 justify-center">
            {TABS.map((t) => (
              <div
                key={t.to}
                className="relative"
                onMouseEnter={() => t.children && setDropdown(t.to)}
                onMouseLeave={() => t.children && setDropdown(null)}
              >
                <Link
                  to={t.to}
                  data-testid={t.testid}
                  className={`relative inline-flex items-center gap-1 px-3 py-2 text-sm font-medium tracking-wide transition whitespace-nowrap ${
                    activeTop(t.to) ? "text-ink" : "text-ink-50 hover:text-ink"
                  }`}
                >
                  {t.label}
                  {t.children && <span className="text-[10px] opacity-70">▾</span>}
                  {activeTop(t.to) && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-3 right-3 h-px bg-saffron"
                    />
                  )}
                </Link>

                <AnimatePresence>
                  {t.children && dropdown === t.to && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 top-full pt-3"
                    >
                      <div className="min-w-[220px] rounded-xl border border-ivory-300 bg-ivory shadow-[0_20px_60px_-30px_rgba(10,15,13,0.35)] overflow-hidden">
                        {t.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={c.testid}
                            className="block px-4 py-3 text-sm text-ink hover:bg-ivory-200 transition"
                            onClick={() => setDropdown(null)}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            {user ? (
              <>
                <span
                  data-testid={NAV.userBadge}
                  className="hidden sm:inline text-sm text-ink-50"
                >
                  <span className="text-ink-50/70">Namaste,&nbsp;</span>
                  <span className="text-ink font-medium">{user.name.split(" ")[0]}</span>
                </span>
                <button
                  onClick={logout}
                  data-testid={NAV.ctaLogout}
                  className="text-sm text-ink-50 hover:text-ink px-3 py-2"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={() => { setAuthTab("login"); setAuthOpen(true); }}
                data-testid={NAV.ctaLogin}
                className="hidden sm:inline text-sm text-ink-50 hover:text-ink px-3 py-2"
              >
                Sign in
              </button>
            )}
            <button
              data-testid={NAV.ctaStart}
              onClick={() => {
                if (user) navigate("/my-tree");
                else { setAuthTab("register"); setAuthOpen(true); }
              }}
              className="hidden sm:inline group relative rounded-full bg-tulsi text-ivory px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-tulsi-deep active:scale-[0.97] transition"
            >
              Start your tree
              <span className="ml-1 inline-block group-hover:translate-x-1 transition-transform">→</span>
            </button>

            <button
              className="xl:hidden text-ink"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                {mobileOpen ? (
                  <path d="M6 6l12 12M6 18L18 6" />
                ) : (
                  <>
                    <path d="M4 7h16M4 12h16M4 17h16" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="xl:hidden border-t border-ivory-300 bg-ivory/95 backdrop-blur-xl overflow-hidden"
            >
              <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex flex-col">
                {TABS.map((t) => (
                  <Link
                    key={t.to}
                    to={t.to}
                    className={`py-3 border-b border-ivory-200 text-base ${activeTop(t.to) ? "text-ink font-medium" : "text-ink-50"}`}
                  >
                    {t.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab={authTab} />
    </>
  );
}
